import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Package, ArrowDownLeft, ArrowUpRight, MapPin, 
  Check, X, MessageSquare, Trash2, Loader2, ArrowRight, Repeat
} from 'lucide-react';
import { formatDate, getStatusColor, getConditionColor } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [myListings, setMyListings] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all data with individual error handling
      const [listingsRes, incomingRes, outgoingRes] = await Promise.allSettled([
        axios.get(`${API}/listings/my`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/trade-requests/incoming`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/trade-requests/outgoing`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Set data even if some requests fail
      setMyListings(listingsRes.status === 'fulfilled' ? listingsRes.value.data : []);
      setIncomingRequests(incomingRes.status === 'fulfilled' ? incomingRes.value.data : []);
      setOutgoingRequests(outgoingRes.status === 'fulfilled' ? outgoingRes.value.data : []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load some dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (requestId) => {
    setActionLoading(requestId);
    try {
      await axios.put(
        `${API}/trade-requests/${requestId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Trade accepted! Both items are now marked as traded.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept trade');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (requestId) => {
    setActionLoading(requestId);
    try {
      await axios.put(
        `${API}/trade-requests/${requestId}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Trade request declined');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline trade');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      await axios.delete(`${API}/listings/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Listing deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete listing');
    }
  };

  // Safe count functions
  const getPendingIncomingCount = () => {
    return Array.isArray(incomingRequests) 
      ? incomingRequests.filter(r => r?.status === 'pending').length 
      : 0;
  };

  const getPendingOutgoingCount = () => {
    return Array.isArray(outgoingRequests) 
      ? outgoingRequests.filter(r => r?.status === 'pending').length 
      : 0;
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#F9F9F7]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F7] animate-fadeIn" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-stone-900 mb-1">Dashboard</h1>
            <p className="text-stone-600">Welcome back, {user?.name}</p>
          </div>
          <Link to="/create-listing">
            <Button className="bg-[#008080] hover:bg-[#008080]/90 rounded-full" data-testid="new-listing-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Listing
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-stone-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500 mb-1">My Listings</p>
                <p className="text-3xl font-bold text-stone-900">{myListings?.length || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#008080]/10 flex items-center justify-center">
                <Package className="w-6 h-6 text-[#008080]" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-stone-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500 mb-1">Incoming Requests</p>
                <p className="text-3xl font-bold text-stone-900">{getPendingIncomingCount()}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <ArrowDownLeft className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-stone-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500 mb-1">Outgoing Requests</p>
                <p className="text-3xl font-bold text-stone-900">{getPendingOutgoingCount()}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <ArrowUpRight className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="listings" className="space-y-6">
          <TabsList className="bg-white border border-stone-100 p-1 rounded-xl">
            <TabsTrigger 
              value="listings" 
              className="rounded-lg data-[state=active]:bg-[#008080] data-[state=active]:text-white"
              data-testid="tab-listings"
            >
              My Listings ({myListings?.length || 0})
            </TabsTrigger>
            <TabsTrigger 
              value="incoming"
              className="rounded-lg data-[state=active]:bg-[#008080] data-[state=active]:text-white"
              data-testid="tab-incoming"
            >
              Incoming ({getPendingIncomingCount()})
            </TabsTrigger>
            <TabsTrigger 
              value="outgoing"
              className="rounded-lg data-[state=active]:bg-[#008080] data-[state=active]:text-white"
              data-testid="tab-outgoing"
            >
              Outgoing ({getPendingOutgoingCount()})
            </TabsTrigger>
          </TabsList>

          {/* My Listings Tab */}
          <TabsContent value="listings" className="space-y-4">
            {!myListings || myListings.length === 0 ? (
              <div className="empty-state bg-white rounded-2xl border border-stone-100">
                <Package className="w-12 h-12 text-stone-300 mb-4" />
                <h3 className="text-lg font-semibold text-stone-900 mb-2">No listings yet</h3>
                <p className="text-stone-600 mb-6">Start trading by listing your first item</p>
                <Link to="/create-listing">
                  <Button className="bg-[#008080] hover:bg-[#008080]/90 rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Listing
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid gap-4">
                {myListings.map((listing) => (
                  <div 
                    key={listing.id}
                    className="bg-white rounded-2xl border border-stone-100 p-4 flex flex-col sm:flex-row gap-4"
                    data-testid={`my-listing-${listing.id}`}
                  >
                    <Link to={`/listing/${listing.id}`} className="flex-shrink-0">
                      <div className="w-full sm:w-32 h-32 rounded-xl overflow-hidden bg-stone-100">
                        {listing.images?.[0] ? (
                          <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-stone-400">
                            No Image
                          </div>
                        )}
                      </div>
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(listing.status)}`}>
                              {listing.status}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getConditionColor(listing.condition)}`}>
                              {listing.condition}
                            </span>
                          </div>
                          <Link to={`/listing/${listing.id}`}>
                            <h3 className="font-semibold text-stone-900 hover:text-[#008080] transition-colors">
                              {listing.title}
                            </h3>
                          </Link>
                          <p className="text-sm text-stone-500 line-clamp-1 mt-1">{listing.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-stone-400">
                            <span className="flex items-center">
                              <MapPin className="w-3 h-3 mr-1" />
                              {listing.location}
                            </span>
                            <span>{formatDate(listing.created_at)}</span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteListing(listing.id)}
                          className="text-stone-400 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-listing-${listing.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Incoming Requests Tab */}
          <TabsContent value="incoming" className="space-y-4">
            {!incomingRequests || incomingRequests.length === 0 ? (
              <div className="empty-state bg-white rounded-2xl border border-stone-100">
                <ArrowDownLeft className="w-12 h-12 text-stone-300 mb-4" />
                <h3 className="text-lg font-semibold text-stone-900 mb-2">No incoming requests</h3>
                <p className="text-stone-600">When someone wants to trade with you, it will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {incomingRequests.map((request) => (
                  <TradeRequestCard
                    key={request.id}
                    request={request}
                    type="incoming"
                    onAccept={() => handleAccept(request.id)}
                    onDecline={() => handleDecline(request.id)}
                    actionLoading={actionLoading === request.id}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Outgoing Requests Tab */}
          <TabsContent value="outgoing" className="space-y-4">
            {!outgoingRequests || outgoingRequests.length === 0 ? (
              <div className="empty-state bg-white rounded-2xl border border-stone-100">
                <ArrowUpRight className="w-12 h-12 text-stone-300 mb-4" />
                <h3 className="text-lg font-semibold text-stone-900 mb-2">No outgoing requests</h3>
                <p className="text-stone-600 mb-6">Browse items and send trade requests</p>
                <Link to="/browse">
                  <Button className="bg-[#008080] hover:bg-[#008080]/90 rounded-full">
                    Browse Items
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {outgoingRequests.map((request) => (
                  <TradeRequestCard
                    key={request.id}
                    request={request}
                    type="outgoing"
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function TradeRequestCard({ request, type, onAccept, onDecline, actionLoading }) {
  const navigate = useNavigate();
  const isIncoming = type === 'incoming';
  
  // Safely access nested properties
  const senderListing = request?.sender_listing || {};
  const receiverListing = request?.receiver_listing || {};
  
  return (
    <div 
      className="bg-white rounded-2xl border border-stone-100 overflow-hidden"
      data-testid={`trade-request-${request.id}`}
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between bg-stone-50/50">
        <div className="flex items-center gap-3">
          <Repeat className="w-5 h-5 text-[#008080]" />
          <span className="font-medium text-stone-900">Trade Request</span>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
            {request.status}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-stone-500">{formatDate(request.created_at)}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/messages/${request.id}`)}
            className="rounded-full"
            data-testid={`message-btn-${request.id}`}
          >
            <MessageSquare className="w-4 h-4 mr-1" />
            Chat
          </Button>
        </div>
      </div>

      {/* Trade Visualization - Side by Side */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr] gap-4 md:gap-6 items-center">
          
          {/* Offered Item (What they're offering) */}
          <div className="bg-stone-50 rounded-xl p-4">
            <p className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-3">
              {isIncoming ? "They're Offering" : "You're Offering"}
            </p>
            <div className="flex gap-4">
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-stone-200 flex-shrink-0">
                {senderListing.images?.[0] ? (
                  <img 
                    src={senderListing.images[0]} 
                    alt={senderListing.title} 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-400 text-xs">
                    No image
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-stone-900 line-clamp-2">
                  {senderListing.title || 'Unknown Item'}
                </h4>
                <p className="text-sm text-stone-500 mt-1">
                  From: {request.sender_name || 'Unknown'}
                </p>
                {senderListing.condition && (
                  <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${getConditionColor(senderListing.condition)}`}>
                    {senderListing.condition}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-[#008080] text-white flex items-center justify-center">
              <ArrowRight className="w-5 h-5 md:rotate-0 rotate-90" />
            </div>
          </div>

          {/* Requested Item (What they want) */}
          <div className="bg-stone-50 rounded-xl p-4">
            <p className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-3">
              {isIncoming ? "For Your Item" : "For Their Item"}
            </p>
            <div className="flex gap-4">
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-stone-200 flex-shrink-0">
                {receiverListing.images?.[0] ? (
                  <img 
                    src={receiverListing.images[0]} 
                    alt={receiverListing.title} 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-400 text-xs">
                    No image
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-stone-900 line-clamp-2">
                  {receiverListing.title || 'Unknown Item'}
                </h4>
                <p className="text-sm text-stone-500 mt-1">
                  From: {request.receiver_name || 'Unknown'}
                </p>
                {receiverListing.condition && (
                  <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${getConditionColor(receiverListing.condition)}`}>
                    {receiverListing.condition}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Status Message */}
        {request.status === 'accepted' && (
          <div className="mt-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
            <p className="text-emerald-800 text-sm font-medium flex items-center">
              <Check className="w-4 h-4 mr-2" />
              Trade accepted! Both items have been marked as traded. Use the chat to arrange the exchange.
            </p>
          </div>
        )}

        {request.status === 'declined' && (
          <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-100">
            <p className="text-red-800 text-sm font-medium flex items-center">
              <X className="w-4 h-4 mr-2" />
              This trade request was declined.
            </p>
          </div>
        )}

        {/* Action Buttons for Incoming Pending Requests */}
        {isIncoming && request.status === 'pending' && (
          <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-stone-100">
            <Button
              onClick={onDecline}
              disabled={actionLoading}
              variant="outline"
              className="flex-1 h-12 rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
              data-testid={`decline-btn-${request.id}`}
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <X className="w-4 h-4 mr-2" />
                  Decline Trade
                </>
              )}
            </Button>
            <Button
              onClick={onAccept}
              disabled={actionLoading}
              className="flex-1 h-12 bg-[#008080] hover:bg-[#008080]/90 rounded-full"
              data-testid={`accept-btn-${request.id}`}
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Accept Trade
                </>
              )}
            </Button>
          </div>
        )}

        {/* Outgoing pending status */}
        {!isIncoming && request.status === 'pending' && (
          <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
            <p className="text-amber-800 text-sm">
              Waiting for {request.receiver_name} to respond to your trade request.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
